import SimpleTooltip from '@rio-cloud/rio-uikit/SimpleTooltip';
import type { SortDirectionType } from '@rio-cloud/rio-uikit/utils/SortUtils';
import { PLACEMENT } from '@rio-cloud/rio-uikit/values/Placement';
import type React from 'react';

import type { Column } from '@/components/Table/Table';

interface SortableHeaderWithTooltipProps<T> {
    col: Column<T>;
    sortKey: string;
    sortDirection: SortDirectionType;
    onClick: (event: React.MouseEvent<HTMLTableCellElement>) => void;
}

/**
 * Sortable table header with a tooltip icon next to the label.
 *
 * Renders a custom <th> instead of TableHead because TableHead's nested
 * inline spans prevent text truncation. Here, the label truncates with
 * ellipsis while sort arrows and tooltip icon stay fixed.
 */
export const SortableHeaderWithTooltip = <T,>({
    col,
    sortKey,
    sortDirection,
    onClick,
}: SortableHeaderWithTooltipProps<T>) => {
    const sortClass = col.key === sortKey ? `sort-${sortDirection}` : '';

    return (
        <th
            className={`user-select-none sort-column ${col.width ?? ''}`}
            onClick={onClick}
            data-field={col.key}
            data-sortby={col.key}
        >
            <span className='display-flex align-items-center'>
                <span className={`sort-arrows ${sortClass}`} style={{ flexShrink: 0 }} />
                <span className='overflow-hidden ellipsis-1 min-width-0 white-space-nowrap'>{col.label}</span>
                <SimpleTooltip
                    content={col.headerTooltip}
                    placement={PLACEMENT.BOTTOM_END}
                    width={300}
                    textAlignment='left'
                    innerClassName='padding-15 white-space-normal'
                >
                    <span className='uikit-icon icon-info-sign text-color-dark cursor-pointer margin-left-5' />
                </SimpleTooltip>
            </span>
        </th>
    );
};
